import { useQuery } from '@sixfold/app-data-framework';
import { useErrorHandler } from '@sixfold/app-utils';
import { SpinnerOverlay } from '@sixfold/common-ui';
import { CustomError } from '@sixfold/typed-primitives';
import * as React from 'react';

import { ErrorPage } from '../../components/error_page';
import { ViewerCompaniesQuery } from '../../lib/graphql';
import { Company } from '../entities';
import { viewerCompanies } from '../graphql';
import { useLastViewedCompanyId } from './hooks';

export interface ViewerCompanyProps {
  children?: (company: Company) => React.ReactElement;
}

export const ViewerCompanyContainer: React.FunctionComponent<ViewerCompanyProps> = ({ children }) => {
  const { isLoading, error, companies } = useViewerCompanies();
  const errorHandler = useErrorHandler();
  const [lastViewedCompanyId] = useLastViewedCompanyId();

  if (isLoading) {
    return <SpinnerOverlay />;
  }

  if (error !== undefined && companies === undefined) {
    // Only errors that prevent us from getting any data back should be propagated
    throw error;
  }

  if (children === undefined) {
    return null;
  }

  if (companies === undefined || companies.length === 0) {
    errorHandler?.captureError(new CustomError('Failed to fetch companies for viewer'));
    return <ErrorPage type="not-found" />;
  }

  const lastViewedCompany = companies.find((company) => company.company_id === lastViewedCompanyId);
  const company = lastViewedCompany ?? companies[0];

  return children(company);
};

export function useViewerCompanies(options?: { skip?: boolean }) {
  const result = useQuery<ViewerCompaniesQuery>(viewerCompanies, { skip: options?.skip });

  const companies = React.useMemo<Company[] | undefined>(
    () =>
      result.data?.viewer?.companies?.map((company) => ({
        ...company,
        is_carrier: company.is_carrier === true,
        is_shipper: company.is_shipper === true,
        is_test: company.is_test === true,
        featureFlags: {},
      })) ?? undefined,
    [result.data],
  );

  return React.useMemo(() => ({ companies, isLoading: result.loading, error: result.error }), [result, companies]);
}
